import { Flex, Box, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Modal, useToast } from '@morressier/alembic';
import { useUpdatedUserInfoMutation } from 'src/api/signUpMutation';
import {
  IOrganizationForm,
  useUserDetailsData
} from '../UserRegistrationSteps/StepThree/signupHook';
import OrganizationDetailsForm from '../UserRegistrationSteps/StepTwo/OrgonizationDetailsForm';
import { IAffiliationDetails } from '@publishing-engine/domain/models';

interface EditAffiliationDetailsModalProps {
  affiliation: IAffiliationDetails;
  organizationId: string;
}

const EditAffiliationDetailsModal = ({
  affiliation,
  organizationId
}: EditAffiliationDetailsModalProps) => {
  const updateAffiliationMutation = useUpdatedUserInfoMutation();
  const toast = useToast();

  const { configs } = useUserDetailsData(organizationId || '');

  const newConfig = {
    streetAddress: configs.StreetAddress,
    city: configs.City,
    stateOrRegion: configs.StateOrRegion,
    postalCode: configs.PostalCode,
    department: configs.Department,
    country: configs.Country,
    url: configs.Url,
    organizationName: configs.OrganizationName
  };

  const [isOpen, setIsOpen] = useState(false);

  const methods = useForm<{
    affiliationDetails: IOrganizationForm;
  }>({ mode: 'onChange' });
  const { getValues, handleSubmit, reset } = methods;

  const onClose = () => {
    setIsOpen(false);
    reset({ affiliationDetails: affiliation });
  };

  const constructPayload = () => {
    const { affiliationDetails } = getValues();

    return {
      section: 'AFFILIATIONS',
      // affiliation: affiliationDetails.organizationName,
      // ringgoldId: affiliationDetails?.ringgoldId || '',
      affiliationDetails
    };
  };

  const handleEditAffiliationSubmit = async () => {
    constructPayload();
    try {
      const response = await updateAffiliationMutation.mutateAsync({
        payload: constructPayload()
      });

      if (!response.isSuccess) {
        const message =
          response?.message?.details?.[0]?.message ||
          response?.message?.message ||
          'Internal Server Error';
        toast({
          variant: 'error',
          title: 'Affiliation Error',
          description: message,
          isClosable: true
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Success',
        description: response?.message ?? 'Updated successfully',
        isClosable: true
      });
      onClose();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Internal Server Error',
        isClosable: true
      });
    }
  };

  useEffect(() => {
    reset({ affiliationDetails: affiliation });
  }, [affiliation]);

  return (
    <>
      <Button
        variant="secondary"
        leftIcon={<FontAwesomeIcon icon={faEdit} />}
        onClick={() => setIsOpen(true)}
      >
        Edit Primary Affiliation
      </Button>

      <Modal
        isOpen={isOpen}
        width="700px"
        onClose={() => {}}
        closeButtonProps={{
          visibility: 'hidden'
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleEditAffiliationSubmit)}>
            <Flex direction="column" px={8} pt={8} gap={3}>
              <Box>
                <FontAwesomeIcon icon={faEdit} size="2xl" />
              </Box>
              <Text textStyle="headingGroup">Edit Primary Affiliation</Text>
              <OrganizationDetailsForm
                formConfig={newConfig}
                fieldPrefix="affiliationDetails"
                affiliationDetails={affiliation || null}
              />
              <Flex justify="flex-end" gap={3} my={5}>
                <Button variant="secondary" onClick={onClose}>
                  Exit Without Saving
                </Button>
                <Button
                  type="submit"
                  disabled={updateAffiliationMutation.isLoading}
                >
                  {updateAffiliationMutation.isLoading
                    ? 'Saving...'
                    : 'Save Changes'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default EditAffiliationDetailsModal;
